import { Table, useDefaultSorting } from '@bp/ui-components';
import type { Row } from '@tanstack/react-table';
import { type DocumentType, type FragmentType, getFragmentData, graphql } from 'client/gql';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { niceDate } from '../../utils/dateCalculations';
import { handleFileEntryDownload } from '../../utils/download';
import { BpLink } from '../BpLink/BpLink';

export const FilePreviewTableFieldsDocument = graphql(`
  fragment FilePreviewTableFields on FileEntry {
    uuid
    __typename
    filename
    updated
    downloadToken
  }
`);

export type FileEntryPreviewTableType = DocumentType<typeof FilePreviewTableFieldsDocument>;
type FilePreviewTableProps = {
  query: FragmentType<typeof FilePreviewTableFieldsDocument>[] | undefined;
  hasNote?: boolean;
  className?: string | undefined;
  isGroupEditor: boolean;
};

export const FilePreviewTable: FC<FilePreviewTableProps> = ({ query, hasNote, className, isGroupEditor }) => {
  const { t } = useTranslation();
  const { sorting, onSortingChange } = useDefaultSorting([{ id: 'name', desc: false }]);
  const data = getFragmentData(FilePreviewTableFieldsDocument, query) ?? [];

  return (
    <Table<FileEntryPreviewTableType>
      maxHeight={hasNote ? 105 : 150}
      canScroll
      className={className}
      hideHeader
      showSort
      sorting={sorting}
      onSortingChange={onSortingChange}
      customPadding='var(--spacing-6)'
      customRowHeight='var(--list-row-height)'
      customLastColSpacing='var(--spacing-3)'
      lastColWidth='100px'
      isDesktop={true}
      data={data}
      columns={[
        {
          id: 'name',
          accessorKey: 'name',
          size: 300,
          cell: ({ row }) => {
            return (
              <BpLink
                value={row.original.filename}
                onNavigate={() => {
                  const { downloadToken, filename, uuid } = row.original;
                  void handleFileEntryDownload({
                    downloadToken,
                    filename,
                    uuid,
                  });
                }}
                isEditorOfCourse={isGroupEditor}
              />
            );
          },
        },
      ]}
      emptyStateSettings={{
        hideIcon: true,
        size: 'small',
        title: '',
        subtitle: t('files.noFiles'),
        forcedHeight: hasNote ? '60px' : '150px',
        padding: hasNote ? 'none' : 'xl',
        fitParent: true,
      }}
      lastCol={(row: Row<FileEntryPreviewTableType>) => {
        return <>{niceDate(row.original.updated, 'short')}</>;
      }}
    />
  );
};
